import { useEffect, useRef, useState } from 'react'
import { cn } from '../cn'
import { IconDotsVertical } from '../Icons'

export interface RowAction<RowT> {
  id: string
  label: string
  onSelect: (row: RowT) => void
  disabled?: boolean
  destructive?: boolean
}

export interface RowActionsProps<RowT> {
  row: RowT
  actions: RowAction<RowT>[]
  className?: string
}

export function RowActions<RowT>({ row, actions, className = '' }: RowActionsProps<RowT>) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  if (actions.length === 0) return null

  return (
    <div ref={rootRef} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation()
          setOpen((current) => !current)
        }}
        className="inline-flex min-h-tap min-w-tap items-center justify-center rounded-control text-ink-muted hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus"
      >
        <IconDotsVertical size={18} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-dropdown mt-1 min-w-36 rounded-control border border-line bg-white py-1 shadow-pop animate-fade-in motion-reduce:animate-none">
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              role="menuitem"
              disabled={action.disabled}
              onClick={(event) => {
                event.stopPropagation()
                action.onSelect(row)
                setOpen(false)
              }}
              className={cn(
                'block w-full px-3 py-2 text-left text-sm hover:bg-whisper disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:shadow-focus',
                action.destructive ? 'text-err-ink' : 'text-ink-body',
              )}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
